import React, { useState, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Lightweight OverlayTrigger + Tooltip + Popover (react-bootstrap-compatible surface).
// Hover/focus (or click) reveals a positioned overlay.
//
// The overlay is rendered in a portal on <body> with position:fixed so it can never be
// clipped by an ancestor's overflow:hidden (e.g. the home dashboard's grouped cards) or
// covered by a neighbouring component. Default placement is "right"; if the overlay would
// run off the viewport it flips to the opposite side and is clamped back on-screen.
type Placement = 'top' | 'bottom' | 'left' | 'right';

const GAP = 8; // px between the trigger and the overlay

export const Tooltip: React.FC<React.HTMLAttributes<HTMLDivElement> & { id?: string }> = ({ className = '', children, ...rest }) => (
  <div className={`tw-max-w-xs tw-rounded tw-bg-content tw-px-2 tw-py-1 tw-text-xs tw-text-white tw-shadow-lg ${className}`} {...rest}>
    {children}
  </div>
);

type PopoverPartProps = React.HTMLAttributes<HTMLElement> & { as?: React.ElementType };
interface PopoverType extends React.FC<React.HTMLAttributes<HTMLDivElement> & { id?: string }> {
  Header: React.FC<PopoverPartProps>;
  Body: React.FC<PopoverPartProps>;
  Title: React.FC<PopoverPartProps>;
}
export const Popover = (({ className = '', children, ...rest }) => (
  <div className={`tw-min-w-[12rem] tw-rounded tw-border tw-border-line tw-bg-surface tw-shadow-lg ${className}`} {...rest}>
    {children}
  </div>
)) as PopoverType;
Popover.Header = ({ as, className = '', ...rest }) => {
  const Comp: React.ElementType = as || 'div';
  return <Comp className={`tw-border-b tw-border-line tw-bg-surface-alt tw-px-3 tw-py-2 tw-font-medium ${className}`} {...rest} />;
};
Popover.Title = Popover.Header;
Popover.Body = ({ as, className = '', ...rest }) => {
  const Comp: React.ElementType = as || 'div';
  return <Comp className={`tw-px-3 tw-py-2 ${className}`} {...rest} />;
};

/** Fixed-viewport coordinates for the overlay given the trigger rect, overlay size and placement. */
function computePosition(
  rect: DOMRect,
  size: { w: number; h: number },
  placement: Placement,
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const place = (p: Placement) => {
    switch (p) {
      case 'right':
        return { top: rect.top + rect.height / 2 - size.h / 2, left: rect.right + GAP };
      case 'left':
        return { top: rect.top + rect.height / 2 - size.h / 2, left: rect.left - GAP - size.w };
      case 'top':
        return { top: rect.top - GAP - size.h, left: rect.left + rect.width / 2 - size.w / 2 };
      case 'bottom':
      default:
        return { top: rect.bottom + GAP, left: rect.left + rect.width / 2 - size.w / 2 };
    }
  };

  let pos = place(placement);
  // Flip to the opposite side if the preferred placement runs off-screen.
  if (placement === 'right' && pos.left + size.w > vw - 4) pos = place('left');
  else if (placement === 'left' && pos.left < 4) pos = place('right');
  else if (placement === 'top' && pos.top < 4) pos = place('bottom');
  else if (placement === 'bottom' && pos.top + size.h > vh - 4) pos = place('top');

  // Clamp within the viewport so it's never partially hidden.
  pos.left = Math.max(4, Math.min(pos.left, vw - size.w - 4));
  pos.top = Math.max(4, Math.min(pos.top, vh - size.h - 4));
  return pos;
}

/**
 * True on a device without hover - a phone or a tablet driven by touch. Detected with the CSS
 * media features, never the user agent (W-iP13). An iPad with a trackpad reports hover and keeps
 * the desktop behaviour.
 */
export function isHoverlessDevice(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(hover: none)').matches;
  } catch {
    return false;
  }
}

/** Interactive elements whose own tap action must never be delayed by a tooltip. */
const INTERACTIVE = 'a[href],button,input,select,textarea,summary,[role="button"],[role="link"],[role="checkbox"],[role="switch"],[role="menuitem"],[role="tab"]';

/** True when focus arrived by keyboard. Engines without :focus-visible count every focus. */
function isKeyboardFocus(el: HTMLElement): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

export interface OverlayTriggerProps {
  placement?: Placement;
  overlay: React.ReactElement;
  trigger?: 'hover' | 'click' | 'focus' | Array<'hover' | 'click' | 'focus'> | null;
  show?: boolean;
  defaultShow?: boolean;
  children: React.ReactElement;
  /** Classes for the wrapper span. Default `tw-inline-flex`; ClampText needs a block that can shrink. */
  wrapperClassName?: string;
  /**
   * V3-040 / NAV-11: for an icon-only link or button whose tooltip is its NAME (the navbar's
   * Message Center icon). On touch the tap still performs the action on the first tap (TCH-02),
   * and the name is shown briefly as well, so it is readable on tap. Never set it on a tooltip
   * that holds anything but the control's name.
   */
  labelOnTap?: boolean;
}

/** How long a tap-shown name label stays up (labelOnTap). */
export const TAP_LABEL_MS = 1500;
export const OverlayTrigger: React.FC<OverlayTriggerProps> = ({
  placement = 'right',
  overlay,
  trigger = ['hover', 'focus'],
  show,
  children,
  wrapperClassName = 'tw-inline-flex',
  labelOnTap = false,
}) => {
  // Hover, keyboard focus and click are tracked separately so one ending cannot be undone by
  // another lingering. Previously a single `open` flag was set by ANY of them, so a mouse click
  // on a control inside the trigger (the HE grid's fuel-surcharge checkbox) focused it, and the
  // tooltip reopened on the next window refocus with no mouseleave ever coming to close it -
  // the "sticky fuel tooltip" (L6).
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const overlayRef = useRef<HTMLSpanElement>(null);
  // Set by the pointerdown that starts a touch, so the compatibility mouse events a browser fires
  // after a tap (mouseenter, then click) are not mistaken for a desktop hover.
  const touchRef = useRef(false);
  const labelTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (labelTimer.current) clearTimeout(labelTimer.current);
  }, []);
  const open = hovered || focused || clicked;
  const shown = show !== undefined ? show : open;
  const listed = (Array.isArray(trigger) ? trigger : trigger != null ? [trigger] : []).filter(
    (item): item is 'hover' | 'click' | 'focus' => item === 'hover' || item === 'click' || item === 'focus',
  );
  const triggers = listed.length > 0 ? listed : ['hover', 'focus'];

  const closeAll = useCallback(() => {
    setHovered(false);
    setFocused(false);
    setClicked(false);
  }, []);

  const reposition = useCallback(() => {
    if (!triggerRef.current || !overlayRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const o = overlayRef.current.getBoundingClientRect();
    setCoords(computePosition(rect, { w: o.width, h: o.height }, placement));
  }, [placement]);

  // Measure and place once the overlay is in the DOM; keep it anchored on scroll/resize.
  useLayoutEffect(() => {
    if (!shown) {
      setCoords(null);
      return;
    }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [shown, reposition]);

  // Escape always dismisses, and a press anywhere outside the trigger does too. The second is
  // what closes a tooltip on touch, where no mouseleave ever arrives after a tap.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAll();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (!triggerRef.current?.contains(e.target as Node)) closeAll();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, closeAll]);

  // Touch (W-iP13). There is no hover on a touch screen, so:
  //   - a standalone info trigger (an (i) icon, an expiry dot, truncated text) opens on tap and
  //     closes on a tap anywhere else (the outside-pointerdown handler above);
  //   - a trigger that is, or wraps, a link or a button never opens a tooltip on touch. The tap
  //     performs the control's own action on the FIRST tap - a tooltip must never block, delay
  //     or require a second tap.
  // Desktop behaviour is unchanged: hover and keyboard focus, exactly as before.
  const isTouch = () => touchRef.current || isHoverlessDevice();
  // Checked through ancestors too: an (i) inside a button is still part of that button's tap.
  const isInteractiveTap = (target: EventTarget | null) =>
    target instanceof Element && !!target.closest(INTERACTIVE);

  const handlers: React.HTMLAttributes<HTMLSpanElement> = {};
  // pointerenter precedes the compatibility mouseenter, so it decides what that mouseenter means.
  handlers.onPointerEnter = (e) => {
    touchRef.current = e.pointerType === 'touch' || e.pointerType === 'pen';
  };
  handlers.onPointerDown = (e) => {
    touchRef.current = e.pointerType === 'touch' || e.pointerType === 'pen';
    // A press inside the trigger (ticking the checkbox it wraps) ends the hover tooltip rather
    // than leaving it over the control being used. Click-triggered overlays toggle instead.
    if (triggers.includes('hover') && !triggers.includes('click')) setHovered(false);
  };
  if (triggers.includes('hover')) {
    handlers.onMouseEnter = () => {
      if (isTouch()) return;
      setHovered(true);
    };
    handlers.onMouseLeave = () => setHovered(false);
  }
  if (triggers.includes('focus')) {
    // Keyboard focus only: focus that came from a mouse click is not a request for the
    // tooltip, and hover already covers the pointer case.
    handlers.onFocus = (e) => {
      if (isKeyboardFocus(e.target as HTMLElement)) setFocused(true);
    };
    handlers.onBlur = () => setFocused(false);
  }
  if (triggers.includes('click')) {
    handlers.onClick = () => setClicked((o) => !o);
  } else {
    handlers.onClick = (e) => {
      if (!isTouch()) return;
      if (isInteractiveTap(e.target)) {
        // The control has already acted (nothing here prevents it); a name label may show too.
        if (labelOnTap) {
          setClicked(true);
          if (labelTimer.current) clearTimeout(labelTimer.current);
          labelTimer.current = setTimeout(() => setClicked(false), TAP_LABEL_MS);
        }
        return;
      }
      setClicked((o) => !o);
    };
  }

  return (
    <>
      <span ref={triggerRef} className={wrapperClassName} {...handlers}>
        {children}
      </span>
      {shown &&
        createPortal(
          <span
            ref={overlayRef}
            role="tooltip"
            className="tw-z-[1080]"
            style={{
              position: 'fixed',
              top: coords ? coords.top : -9999,
              left: coords ? coords.left : -9999,
              // Hide until measured so it doesn't flash at the wrong spot.
              visibility: coords ? 'visible' : 'hidden',
            }}
          >
            {overlay}
          </span>,
          document.body,
        )}
    </>
  );
};

export default OverlayTrigger;
